import {publicGet} from './publicClient';
import apiClient from '@/shared/services/apiClient';

/**
 * Heterogeneous cart-line shape sent from CheckoutPage to one of the
 * dispatched endpoints below. Each kind goes to its proven endpoint:
 *   product     → /api/shop/checkout/submit (storefront)
 *   custom_kit  → /api/biomatch/custom-kit/checkout (BioMatch v2)
 *   curated_rec → /api/biomatch/enrollments/checkout (BioMatch curated)
 *   membership  → /api/membership/checkout (dispatched by CheckoutPage)
 *
 * Membership + product carts are finalized together through the authenticated
 * membership checkout. Other mixed-kind combinations remain unsupported.
 */
export type CheckoutLineRequest =
    | {kind: 'product'; productId: number; quantity: number}
    | {
          kind: 'custom_kit';
          mode: 'one_time' | 'member';
          assayCodes: string[];
          productIds: number[];
          clientExpectedAmountCents: number;
      }
    | {kind: 'curated_rec'; recommendationId: number; clientExpectedAmountCents: number}
    | {kind: 'membership'; plan: 'monthly' | 'annual'; clientExpectedAmountCents: number};

export interface CheckoutSubmitRequest {
    checkoutAttemptId: string;
    ticketId: string;
    customer: {
        email: string;
        firstName: string;
        lastName: string;
        phone?: string;
        age?: number;
        state?: string;
    };
    shippingAddress: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        zip: string;
        country?: string;
    };
    billingAddress?: {
        line1: string;
        line2?: string;
        city: string;
        state: string;
        zip: string;
        country?: string;
    } | null;
    items: CheckoutLineRequest[];
    deliveryMethod?: 'standard' | 'express';
    promoCode?: string | null;
    giftCardCode?: string | null;
    /** Sum of FE-side `lineCharge` * quantity. BE flags a mismatch. */
    clientExpectedAmountCents?: number;
    /** True when any line is a subscription — BE forces save_card. */
    hasRecurring?: boolean;
}

export interface CheckoutResult {
    orderId: string;
    orderNumber: string;
    status: string;
}

/** Compact discriminator helpers. */
export function lineKinds(items: CheckoutLineRequest[]): Set<CheckoutLineRequest['kind']> {
    return new Set(items.map((i) => i.kind));
}

/**
 * `/api/shop/checkout/submit` accepts only product lines today. The
 * dispatcher in CheckoutPage filters down before calling this.
 */
export interface ProductOnlyCheckoutRequest extends Omit<CheckoutSubmitRequest, 'items'> {
    items: {productId: number; quantity: number}[];
}

export interface CheckoutQuoteResult {
    lines: {productId: number; patientCents: number; employerCents: number; subsidizingOrganizationId: number | null}[];
    employerTotalCents: number;
}

export const CheckoutService = {
    /** Which payment provider is active (+ Stripe publishable key). Does NOT create an intention. */
    async getPaymentConfig(): Promise<{
        provider: 'fortis' | 'stripe';
        publishableKey?: string;
        fortisScriptUrl?: string;
    }> {
        return publicGet<{provider: 'fortis' | 'stripe'; publishableKey?: string; fortisScriptUrl?: string}>(
            '/api/shop/checkout/payment-config'
        );
    },

    async createIntention(): Promise<{provider: 'fortis' | 'stripe'; clientToken: string; publishableKey?: string}> {
        return apiClient.post<{provider: 'fortis' | 'stripe'; clientToken: string; publishableKey?: string}>(
            '/api/shop/checkout/intention',
            {}
        );
    },

    async quote(
        items: {productId: number; quantity: number}[],
        membershipPlan?: 'monthly' | 'annual',
        promoCode?: string | null
    ): Promise<CheckoutQuoteResult> {
        return apiClient.post<CheckoutQuoteResult>('/api/shop/checkout/quote', {items, membershipPlan, promoCode});
    },

    async submitProducts(data: ProductOnlyCheckoutRequest): Promise<CheckoutResult> {
        return apiClient.post<CheckoutResult>('/api/shop/checkout/submit', data);
    },

    async submitCustomKit(input: {
        checkoutAttemptId: string;
        ticketId: string;
        mode: 'one_time' | 'member';
        clientExpectedAmountCents: number;
        shippingAddress?: ShippingAddressPayload;
    }): Promise<{
        enrollmentId: number;
        storefrontOrderId: string;
        paymentStatus: 'CAPTURED' | 'PENDING' | 'FAILED';
        chargedAmountCents: number;
        mode: 'one_time' | 'member';
        nextTestDue: string | null;
    }> {
        return apiClient.post('/api/biomatch/custom-kit/checkout', {
            ticket_id: input.ticketId,
            checkout_attempt_id: input.checkoutAttemptId,
            mode: input.mode,
            save_card: input.mode === 'member',
            client_expected_amount_cents: input.clientExpectedAmountCents,
            shipping_address: input.shippingAddress,
        });
    },

    async submitCuratedRec(input: {
        checkoutAttemptId: string;
        ticketId: string;
        recommendationId: number;
        saveCard?: boolean;
        shippingAddress?: ShippingAddressPayload;
    }): Promise<{
        enrollmentId: number;
        storefrontOrderId: string;
        paymentStatus: 'CAPTURED' | 'PENDING' | 'FAILED';
        patientAmountCents: number;
        employerAmountCents: number;
    }> {
        return apiClient.post('/api/biomatch/enrollments/checkout', {
            recommendation_id: input.recommendationId,
            checkout_attempt_id: input.checkoutAttemptId,
            ticket_id: input.ticketId,
            save_card: input.saveCard ?? false,
            shipping_address: input.shippingAddress,
        });
    },

    /**
     * One charge for any cart mix — membership, storefront products,
     * BioMatch curated recs, and the Custom Lab Package together.
     * Authenticated; the BE prices every line server-side and treats
     * `clientExpectedAmountCents` as a parity check only.
     */
    async submitCombined(input: CombinedCheckoutRequest): Promise<CombinedCheckoutResult> {
        return apiClient.post<CombinedCheckoutResult>('/api/shop/checkout/combined', input);
    },
};

/** Canonical ship-to payload for kit-bearing checkouts (BE: ShippingAddressInput). */
export interface ShippingAddressPayload {
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    zip: string;
}

export interface CombinedCheckoutRequest {
    checkoutAttemptId: string;
    ticketId: string;
    clientExpectedAmountCents?: number;
    /** Ship-to for kit/curated orders; storefront.shippingAddress still wins for the product order. */
    shippingAddress?: ShippingAddressPayload;
    membership?: {plan: 'monthly' | 'annual'};
    storefront?: {
        customer: CheckoutSubmitRequest['customer'];
        shippingAddress: CheckoutSubmitRequest['shippingAddress'];
        billingAddress?: CheckoutSubmitRequest['billingAddress'];
        items: {productId: number; quantity: number}[];
        deliveryMethod?: 'standard' | 'express';
        promoCode?: string | null;
        giftCardCode?: string | null;
    };
    curatedRecIds?: number[];
    customKit?: {mode: 'one_time' | 'member'; saveCard?: boolean};
}

export interface CombinedCheckoutResult {
    paymentStatus: 'CAPTURED';
    chargedAmountCents: number;
    storefrontOrderId: string | null;
    orderNumber: string | null;
    membership: {
        membershipId: number;
        membershipOrderId: string;
        plan: 'monthly' | 'annual';
        currentPeriodEnd: string;
        nextChargeDate: string;
    } | null;
    curatedEnrollments: Array<{enrollmentId: number; storefrontOrderId: string}>;
    customKit: {enrollmentId: number; storefrontOrderId: string} | null;
}
